/**
 * MultiModelPredictionPanel Component
 * Compares predictions from Foundation, Base, and Per-Asset models
 * Designed for SMEs and technicians to understand model agreement/disagreement
 */

import { useMemo } from 'react'
import {
  Brain,
  Layers,
  Target,
  AlertTriangle,
  CheckCircle,
  HelpCircle,
  Info
} from 'lucide-react'
import clsx from 'clsx'

interface ModelPrediction {
  model_type: 'foundation' | 'base' | 'per_asset'
  anomaly_score: number
  is_anomaly: boolean
  confidence: number
  category?: string
  severity?: 'low' | 'medium' | 'high' | 'critical'
  failure_probability?: number
  time_to_failure?: number // hours
}

interface MultiModelPredictionPanelProps {
  assetId: string
  predictions: ModelPrediction[]
  timestamp?: string
  showExplanation?: boolean
  onModelSelect?: (model_type: string) => void
  className?: string
}

export default function MultiModelPredictionPanel({
  assetId,
  predictions,
  timestamp,
  showExplanation = true,
  onModelSelect,
  className
}: MultiModelPredictionPanelProps) {

  const modelInfo = {
    foundation: {
      name: 'Foundation Model',
      description: 'Pre-trained zero-shot model. Good for initial assessment.',
      icon: <Brain size={18} />,
      color: '#8b5cf6' // purple
    },
    base: {
      name: 'Base Model',
      description: 'Trained on all fleet assets. Identifies common patterns.',
      icon: <Layers size={18} />,
      color: '#3b82f6' // blue
    },
    per_asset: {
      name: 'Asset-Specific Model',
      description: 'Fine-tuned for this specific asset. Most accurate.',
      icon: <Target size={18} />,
      color: '#10b981' // green
    }
  }

  // Calculate model agreement
  const agreement = useMemo(() => {
    if (predictions.length < 2) return null

    const anomalyVotes = predictions.filter(p => p.is_anomaly).length
    const normalVotes = predictions.length - anomalyVotes

    if (anomalyVotes === predictions.length) {
      return { level: 'full', type: 'anomaly', message: 'All models detect anomaly' }
    } else if (normalVotes === predictions.length) {
      return { level: 'full', type: 'normal', message: 'All models report normal' }
    } else if (anomalyVotes > normalVotes) {
      return { level: 'partial', type: 'anomaly', message: `${anomalyVotes} of ${predictions.length} models detect anomaly` }
    } else {
      return { level: 'partial', type: 'normal', message: `${normalVotes} of ${predictions.length} models report normal` }
    }
  }, [predictions])

  // Calculate consensus confidence
  const consensusConfidence = useMemo(() => {
    if (predictions.length === 0) return 0
    const avgConfidence = predictions.reduce((sum, p) => sum + p.confidence, 0) / predictions.length

    // Boost confidence if all models agree
    if (agreement?.level === 'full') {
      return Math.min(avgConfidence * 1.1, 1)
    }
    // Reduce confidence if models disagree
    return avgConfidence * 0.85
  }, [predictions, agreement])

  // Get the most severe prediction
  const worstCase = useMemo(() => {
    const severityOrder = { critical: 4, high: 3, medium: 2, low: 1 }
    return predictions.reduce((worst, current) => {
      if (!worst) return current
      const worstSeverity = severityOrder[worst.severity || 'low']
      const currentSeverity = severityOrder[current.severity || 'low']
      return currentSeverity > worstSeverity ? current : worst
    }, predictions[0])
  }, [predictions])

  const getSeverityColor = (severity?: string) => {
    switch (severity) {
      case 'critical': return 'text-status-critical bg-status-critical/10 border-status-critical/30'
      case 'high': return 'text-status-warning bg-status-warning/10 border-status-warning/30'
      case 'medium': return 'text-primary-400 bg-primary-500/10 border-primary-500/30'
      default: return 'text-status-healthy bg-status-healthy/10 border-status-healthy/30'
    }
  }

  const getAnomalyScoreColor = (score: number) => {
    if (score >= 0.8) return 'text-status-critical'
    if (score >= 0.5) return 'text-status-warning'
    if (score >= 0.3) return 'text-primary-400'
    return 'text-status-healthy'
  }

  return (
    <div className={clsx('rounded-xl border border-surface-700 bg-surface-900 overflow-hidden', className)}>
      {/* Header */}
      <div className="p-4 bg-gradient-to-r from-primary-500/10 via-purple-500/10 to-status-healthy/10 border-b border-surface-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-surface-800">
              <Brain size={20} className="text-primary-400" />
            </div>
            <div>
              <h3 className="font-semibold text-surface-100">Multi-Model Prediction</h3>
              <p className="text-xs text-surface-400">
                Asset: <span className="font-mono text-primary-400">{assetId}</span>
                {timestamp && <span className="ml-2">| {timestamp}</span>}
              </p>
            </div>
          </div>

          {/* Agreement Badge */}
          {agreement && (
            <div className={clsx(
              'flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium',
              agreement.level === 'full' && agreement.type === 'anomaly' && 'bg-status-critical/20 text-status-critical',
              agreement.level === 'full' && agreement.type === 'normal' && 'bg-status-healthy/20 text-status-healthy',
              agreement.level === 'partial' && 'bg-status-warning/20 text-status-warning'
            )}>
              {agreement.level === 'full' ? (
                <CheckCircle size={14} />
              ) : (
                <HelpCircle size={14} />
              )}
              {agreement.message}
            </div>
          )}
        </div>
      </div>

      {/* Consensus Summary */}
      <div className="p-4 bg-surface-800/50 border-b border-surface-700">
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-xs text-surface-400 uppercase mb-1">Consensus</p>
            <p className={clsx(
              'text-2xl font-bold font-mono',
              consensusConfidence >= 0.8 ? 'text-status-healthy' :
              consensusConfidence >= 0.6 ? 'text-status-warning' : 'text-status-critical'
            )}>
              {(consensusConfidence * 100).toFixed(0)}%
            </p>
            <p className="text-xs text-surface-500">confidence</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-surface-400 uppercase mb-1">Assessment</p>
            <div className={clsx(
              'inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium',
              worstCase?.is_anomaly ? 'bg-status-critical/10 text-status-critical' : 'bg-status-healthy/10 text-status-healthy'
            )}>
              {worstCase?.is_anomaly ? <AlertTriangle size={14} /> : <CheckCircle size={14} />}
              {worstCase?.is_anomaly ? 'Anomaly Detected' : 'Normal Operation'}
            </div>
          </div>
          <div className="text-center">
            <p className="text-xs text-surface-400 uppercase mb-1">Worst Case</p>
            {worstCase?.severity && (
              <span className={clsx(
                'inline-block px-2 py-1 rounded text-xs font-medium border',
                getSeverityColor(worstCase.severity)
              )}>
                {worstCase.severity.toUpperCase()}
              </span>
            )}
            {worstCase?.time_to_failure && (
              <p className="text-xs text-surface-400 mt-1">
                ~{worstCase.time_to_failure}h to potential failure
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Model Predictions Grid */}
      <div className="p-4">
        <h4 className="text-xs font-semibold text-surface-400 uppercase mb-3">Individual Model Results</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {predictions.map((pred) => {
            const info = modelInfo[pred.model_type]
            return (
              <div
                key={pred.model_type}
                onClick={() => onModelSelect?.(pred.model_type)}
                className={clsx(
                  'p-4 rounded-lg border transition-all',
                  pred.is_anomaly
                    ? 'border-status-critical/30 bg-status-critical/5'
                    : 'border-surface-700 bg-surface-800/50',
                  onModelSelect && 'cursor-pointer hover:border-primary-500/50'
                )}
              >
                {/* Model Header */}
                <div className="flex items-center gap-2 mb-3">
                  <div
                    className="p-1.5 rounded"
                    style={{ backgroundColor: `${info.color}20`, color: info.color }}
                  >
                    {info.icon}
                  </div>
                  <div>
                    <h5 className="font-medium text-surface-200 text-sm">{info.name}</h5>
                    <p className="text-xs text-surface-500">{info.description}</p>
                  </div>
                </div>

                {/* Anomaly Score */}
                <div className="mb-3">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-surface-400">Anomaly Score</span>
                    <span className={clsx('font-mono font-medium', getAnomalyScoreColor(pred.anomaly_score))}>
                      {(pred.anomaly_score * 100).toFixed(1)}%
                    </span>
                  </div>
                  <div className="h-2 bg-surface-700 rounded-full overflow-hidden">
                    <div
                      className={clsx(
                        'h-full rounded-full transition-all',
                        pred.anomaly_score >= 0.8 ? 'bg-status-critical' :
                        pred.anomaly_score >= 0.5 ? 'bg-status-warning' :
                        pred.anomaly_score >= 0.3 ? 'bg-primary-500' : 'bg-status-healthy'
                      )}
                      style={{ width: `${pred.anomaly_score * 100}%` }}
                    />
                  </div>
                </div>

                {/* Confidence */}
                <div className="flex items-center justify-between text-xs mb-2">
                  <span className="text-surface-400">Confidence</span>
                  <span className="font-mono text-surface-300">{(pred.confidence * 100).toFixed(0)}%</span>
                </div>

                {/* Category & Severity */}
                <div className="flex items-center gap-2 flex-wrap">
                  {pred.category && (
                    <span className="px-2 py-0.5 text-xs rounded bg-surface-700 text-surface-300">
                      {pred.category}
                    </span>
                  )}
                  {pred.severity && (
                    <span className={clsx(
                      'px-2 py-0.5 text-xs rounded border',
                      getSeverityColor(pred.severity)
                    )}>
                      {pred.severity}
                    </span>
                  )}
                </div>

                {/* Failure Prediction */}
                {pred.failure_probability !== undefined && pred.failure_probability > 0.1 && (
                  <div className="mt-3 p-2 bg-surface-900 rounded text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-surface-400">Failure Risk</span>
                      <span className={clsx(
                        'font-mono font-medium',
                        pred.failure_probability >= 0.7 ? 'text-status-critical' :
                        pred.failure_probability >= 0.4 ? 'text-status-warning' : 'text-surface-300'
                      )}>
                        {(pred.failure_probability * 100).toFixed(0)}%
                      </span>
                    </div>
                    {pred.time_to_failure && (
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-surface-400">Est. Time</span>
                        <span className="text-surface-300">{pred.time_to_failure}h</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Explanation for SMEs */}
      {showExplanation && (
        <div className="p-4 bg-surface-800/30 border-t border-surface-700">
          <div className="flex items-start gap-2">
            <Info size={16} className="text-primary-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-surface-400">
              <p className="font-medium text-surface-300 mb-1">Understanding the Models:</p>
              <ul className="space-y-1">
                <li><span className="text-purple-400">Foundation</span> provides quick baseline assessment without prior training.</li>
                <li><span className="text-blue-400">Base Model</span> identifies patterns common across all fleet assets.</li>
                <li><span className="text-green-400">Asset-Specific</span> is most accurate as it's trained on this unit's history.</li>
              </ul>
              <p className="mt-2 text-surface-500">
                When all models agree, confidence is high. Disagreement warrants closer inspection.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
